import { Button as CommonUiButton, Input, Select, Stack } from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import React from 'react';

import { Button } from '../../components/button';
import { ConfirmButton } from '../../components/confirm_button';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import { Form as FormFactory } from '../../lib/form';
import { CompanyUserQuery, UserPersonaInCompany, UserRoleInCompany } from '../../lib/graphql';
import { beautifyString } from '../../lib/util/string';
import { Routes } from '../../routes';

export type UserInCompanyFormData = {
  first_name?: string;
  last_name?: string;
  email: string;
  role: UserRoleInCompany;
  persona: UserPersonaInCompany | '';
};

export interface UserInCompanyViewDataProps {
  data?: CompanyUserQuery['company'];
}

interface RouteProps {
  routeProps: {
    push: (path: string) => void;
    goBack: () => void;
  };
}

type Props = UserInCompanyViewDataProps &
  RouteProps & {
    company: { company_id: string };
    isNewUser: boolean;
    addUserToCompany?: (input: {
      email: string;
      companyId: string;
      role?: UserRoleInCompany;
      firstName?: string | null;
      lastName?: string | null;
    }) => Promise<{ userId: string; companyId: string } | null>;
    removeUserFromCompany?: (input: { userId: string; companyId: string }) => Promise<void>;
    changeUserRoleInCompany?: (input: { userId: string; companyId: string; role: UserRoleInCompany }) => Promise<void>;
    changeUserMetadataInCompany?: (input: {
      userId: string;
      companyId: string;
      metadata: { persona: UserPersonaInCompany | null } | null;
    }) => Promise<void>;
  };

class Form extends FormFactory<UserInCompanyFormData> {}

export const UserInCompanyView: React.FC<Props> = ({
  routeProps,
  addUserToCompany,
  removeUserFromCompany,
  changeUserRoleInCompany,
  changeUserMetadataInCompany,
  data,
  company,
  isNewUser,
}) => {
  const userInCompany = data?.user ?? null;
  const hasCompanyUserEditPermission = useHasScopes([BackofficeScope.companyWriteUsers]);
  const isPrivilegedUser = !useIsAdmin() || hasCompanyUserEditPermission;

  if (!isNewUser && userInCompany === null) {
    return <div>This user does not belong to this company</div>;
  }

  return (
    <React.Fragment>
      {userInCompany !== null && (
        <h3>
          <span style={{ marginRight: 20 }}>
            {`#${userInCompany.user.user_id} ${userInCompany.user.first_name} ${userInCompany.user.last_name}`.trim()}
          </span>
          <CommonUiButton
            onClick={() => routeProps.push(Routes.User.generatePath({ user_id: userInCompany.user.user_id }))}
            style={{ float: 'right' }}>
            View user
          </CommonUiButton>
        </h3>
      )}
      <Form
        validateFields={{ email: { exist: 'Missing email' } }}
        initialValues={{
          first_name: userInCompany?.user.first_name ?? '',
          last_name: userInCompany?.user.last_name ?? '',
          email: userInCompany?.user.email ?? '',
          role: userInCompany?.role ?? UserRoleInCompany.REGULAR_USER,
          persona: userInCompany?.metadata?.persona ?? '',
        }}
        onSubmit={async (form) => {
          if (userInCompany === null && addUserToCompany !== undefined) {
            const createdUserInCompany = await addUserToCompany({
              email: form.email,
              companyId: company.company_id,
              firstName: form.first_name,
              lastName: form.last_name,
              role: form.role,
            });

            if (createdUserInCompany !== null && changeUserMetadataInCompany !== undefined && form.persona !== '') {
              // Metadata must be saved separately (as the mutation for doing so is BO limited)
              await changeUserMetadataInCompany({
                userId: createdUserInCompany.userId,
                companyId: company.company_id,
                metadata: { persona: form.persona },
              });
            }
          }

          if (userInCompany !== null) {
            // Existing user was edited
            if (changeUserRoleInCompany !== undefined && userInCompany.role !== form.role) {
              await changeUserRoleInCompany({
                userId: userInCompany.user.user_id,
                companyId: company.company_id,
                role: form.role,
              });
            }

            if (changeUserMetadataInCompany !== undefined && userInCompany?.metadata?.persona !== form.persona) {
              await changeUserMetadataInCompany({
                userId: userInCompany.user.user_id,
                companyId: company.company_id,
                metadata: form.persona !== '' ? { persona: form.persona } : null,
              });
            }
          }

          return routeProps.push(`/companies/${company.company_id}/users`);
        }}>
        {({ form, errors, onChange, onSubmit, isSubmittingForm }) => {
          return (
            <form onSubmit={onSubmit}>
              <Stack direction="column" gap="medium">
                <h4
                  style={{
                    borderBottom: '1px solid var(--color-neutral-border-subtle)',
                    paddingBottom: '4px',
                    margin: 0,
                  }}>
                  User information
                </h4>
                <Stack direction="row" gap="medium">
                  <Input
                    label="First Name"
                    name="first_name"
                    placeholder="First Name"
                    value={form.first_name}
                    onChange={({ value }) => onChange({ name: 'first_name', value })}
                    disabled={!isPrivilegedUser || addUserToCompany === undefined}
                  />
                  <Input
                    label="Last name"
                    name="last_name"
                    placeholder="Last Name"
                    value={form.last_name}
                    onChange={({ value }) => onChange({ name: 'last_name', value })}
                    disabled={!isPrivilegedUser || addUserToCompany === undefined}
                  />
                </Stack>
                <div style={{ maxWidth: 400 }}>
                  <Input
                    label={errors.email ?? 'Email'}
                    name="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={({ value }) => onChange({ name: 'email', value })}
                    disabled={!isPrivilegedUser || addUserToCompany === undefined}
                  />
                </div>
                <h4
                  style={{
                    borderBottom: '1px solid var(--color-neutral-border-subtle)',
                    paddingBottom: '4px',
                    margin: 0,
                  }}>
                  Role & Persona
                </h4>
                <Stack direction="row" gap="medium">
                  <Select
                    label="User role in company"
                    labelPosition="block"
                    options={[
                      { value: UserRoleInCompany.REGULAR_USER, label: beautifyString(UserRoleInCompany.REGULAR_USER) },
                      { value: UserRoleInCompany.ADMIN, label: beautifyString(UserRoleInCompany.ADMIN) },
                    ]}
                    value={form.role}
                    disabled={!isPrivilegedUser}
                    onChange={(option) => {
                      if (option !== null) {
                        onChange({ name: 'role', value: option.value });
                      }
                    }}
                  />
                  <Select
                    label="User persona in company"
                    labelPosition="block"
                    isClearable
                    placeholder="Unknown or Not applicable"
                    options={[
                      {
                        value: UserPersonaInCompany.CUSTOMER_SUPPORT,
                        label: beautifyString(UserPersonaInCompany.CUSTOMER_SUPPORT),
                      },
                      {
                        value: UserPersonaInCompany.TRANSPORT_PLANNER,
                        label: beautifyString(UserPersonaInCompany.TRANSPORT_PLANNER),
                      },
                      {
                        value: UserPersonaInCompany.WAREHOUSE_MANAGER,
                        label: beautifyString(UserPersonaInCompany.WAREHOUSE_MANAGER),
                      },
                      { value: UserPersonaInCompany.DEVELOPER, label: beautifyString(UserPersonaInCompany.DEVELOPER) },
                    ]}
                    value={form.persona || null}
                    disabled={!isPrivilegedUser}
                    onChange={(option) => {
                      onChange({ name: 'persona', value: option === null ? '' : option.value });
                    }}
                  />
                </Stack>
                <Stack direction="row" alignItems="center">
                  <Button
                    type="submit"
                    kind="primary"
                    loading={isSubmittingForm}
                    disabled={!isPrivilegedUser || isSubmittingForm}>
                    {addUserToCompany === undefined ? 'Update' : 'Add user to company'}
                  </Button>
                  <Button type="button" onClick={() => routeProps.goBack()}>
                    Cancel
                  </Button>

                  {userInCompany !== null && removeUserFromCompany !== undefined && (
                    <ConfirmButton
                      disabled={!isPrivilegedUser}
                      initialButtonColor="red"
                      label="Remove from company"
                      className="right floated"
                      onConfirm={async () => {
                        await removeUserFromCompany({
                          userId: userInCompany.user.user_id,
                          companyId: company.company_id,
                        });
                        routeProps.push(`/companies/${company.company_id}/users`);
                      }}
                    />
                  )}
                </Stack>
              </Stack>
            </form>
          );
        }}
      </Form>
      {addUserToCompany !== undefined && (
        <div className="helptext">
          <p>Already existing users (potentially belonging to another company) are matched using the email address</p>
        </div>
      )}
    </React.Fragment>
  );
};
